import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from '../api/axios';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
    const [settings, setSettings] = useState({});
    const [darkMode, setDarkMode] = useState(false);
    const [loading, setLoading] = useState(true);

    // Ayarları API'den çeken fonksiyon
    const fetchSettings = async () => {
        try {
            const res = await axios.get('/settings');
            if (res.data) {
                setSettings(res.data);
            }
        } catch (error) {
            console.error('Tema ayarları yüklenemedi:', error);
        } finally {
            setLoading(false);
        }
    };

    // Uygulama ilk açıldığında ayarları ve tema modunu yükle
    useEffect(() => {
        fetchSettings();
        const savedMode = localStorage.getItem('darkMode');
        if (savedMode) {
            setDarkMode(JSON.parse(savedMode));
        }
    }, []);

    // Ayarlar (özellikle renkler) değiştiğinde CSS değişkenlerini ve override'ları güncelle
    useEffect(() => {
        const root = document.documentElement;

        // Site Başlığı Güncelleme
        if (settings.site_title) {
            document.title = settings.site_title;
        }

        // Hex to RGB Helper
        const hexToRgb = (hex) => {
            if (!hex) return null;
            hex = hex.trim();
            if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return null;
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `${r}, ${g}, ${b}`;
        };

        const primaryColor = (settings && settings.primary_color) ? settings.primary_color.trim() : '#17a5b2';
        const secondaryColor = (settings && settings.secondary_color) ? settings.secondary_color.trim() : '#6c757d'; // Daha nötr bir ikincil renk

        // 1. Bootstrap'in ana CSS değişkenlerini ayarla
        root.style.setProperty('--bs-primary', primaryColor);
        root.style.setProperty('--primary-color', primaryColor);
        const primaryRgb = hexToRgb(primaryColor);
        if (primaryRgb) root.style.setProperty('--bs-primary-rgb', primaryRgb);

        root.style.setProperty('--bs-secondary', secondaryColor);
        root.style.setProperty('--secondary-color', secondaryColor);
        const secondaryRgb = hexToRgb(secondaryColor);
        if (secondaryRgb) root.style.setProperty('--bs-secondary-rgb', secondaryRgb);

        // 2. CSS dosyasındaki hardcoded renkleri ezmek için <style> etiketi ekle
        const styleId = 'dynamic-theme-override-styles';
        let styleElement = document.getElementById(styleId);
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }
        
        // Bu kurallar, harici CSS dosyasındaki (.nav-link-custom.active gibi)
        // sabit renk kodlarını geçersiz kılmak için !important kullanır.
        styleElement.innerHTML = `
          .nav-link-custom.active {
            background: ${primaryColor} !important;
            color: #fff !important;
            box-shadow: 0 4px 12px -2px ${primaryColor}80;
          }
          .btn-primary {
            background-color: ${primaryColor} !important;
            border-color: ${primaryColor} !important;
          }
          .btn-check:active+.btn-primary, .btn-check:checked+.btn-primary, .btn-primary.active, .btn-primary:active, .show>.btn-primary.dropdown-toggle {
            background-color: ${primaryColor} !important;
            border-color: ${primaryColor} !important;
            filter: brightness(90%);
          }
          .btn-primary:hover {
            filter: brightness(110%);
          }
          .text-primary {
            color: ${primaryColor} !important;
          }
          .page-item.active .page-link {
            background-color: ${primaryColor} !important;
            border-color: ${primaryColor} !important;
          }
          .form-check-input:checked {
            background-color: ${primaryColor} !important;
            border-color: ${primaryColor} !important;
          }
          .bg-primary-soft {
            background-color: ${primaryColor}20 !important;
          }
        `;

    }, [settings]);

    // Tema modu (dark/light) değiştiğinde body'e class ekle/kaldır
    useEffect(() => {
        if (darkMode) {
            document.body.classList.add('dark-mode');
            document.documentElement.setAttribute('data-bs-theme', 'dark');
        } else {
            document.body.classList.remove('dark-mode');
            document.documentElement.setAttribute('data-bs-theme', 'light');
        }
        localStorage.setItem('darkMode', JSON.stringify(darkMode));
    }, [darkMode]);

    const toggleTheme = () => {
        setDarkMode(prevMode => !prevMode);
    };

    const value = {
        settings,
        darkMode,
        toggleTheme,
        fetchSettings,
        loading
    };

    // Ayarlar yüklenene kadar alt bileşenleri render etme
    if (loading) {
        return <div className="d-flex justify-content-center align-items-center vh-100"><div className="spinner-border text-primary" role="status"></div></div>;
    }

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};
